import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { Ability } from 'ember-can';
import type AbilitiesService from 'ember-can/services/abilities';
import EntryModel from 'garaje/models/entry';
import InviteModel from 'garaje/models/invite';
import type AuthzService from 'garaje/services/authz';
import type CurrentAdminService from 'garaje/services/current-admin';
import type StateService from 'garaje/services/state';
import { BlocklistFilterSource } from 'garaje/utils/enums';
import { GLOBAL_ADMIN, LOCATION_ADMIN, ZONE_ADMIN } from 'garaje/utils/roles';
import { Permission } from 'garaje/utils/ui-permissions';
import _intersection from 'lodash/intersection';

import type { GetCanReviewOrReadBlocklistMatchModel } from './security';

const ALLOWED_ROLES = [GLOBAL_ADMIN, LOCATION_ADMIN, ZONE_ADMIN];
const CAN_MANAGE_PROPERTY = [GLOBAL_ADMIN, ZONE_ADMIN];

type SupportedModelName = 'invite' | 'entry';

export default class BlocklistAbility extends Ability {
  declare model?: GetCanReviewOrReadBlocklistMatchModel;

  @service declare currentAdmin: CurrentAdminService;
  @service declare state: StateService;
  @service declare authz: AuthzService;
  @service declare abilities: AbilitiesService;

  get canApprove(): boolean {
    return this.canReview;
  }

  get canDeny(): boolean {
    return this.canReview;
  }

  get canView(): boolean {
    const { context } = this.model ?? {};
    const hasPermission =
      context === 'property' ? this.canManageProperty : this.abilities.can(`read blocklist for security`, this.model);

    return hasPermission && this.#hasReportsWithMatchingContext;
  }

  get canReview(): boolean {
    const { parent } = this.model ?? {};

    if (parent instanceof EntryModel) return this.canReviewAnEntryApproval;
    if (parent instanceof InviteModel) return this.canReviewAnInviteApproval;

    return this.canReviewAnInviteApproval || this.canReviewAnEntryApproval;
  }

  get canReviewAnEntryApproval(): boolean {
    return this.#canReview('entry');
  }

  get canReviewAnInviteApproval(): boolean {
    return this.#canReview('invite');
  }

  #canReview(modelName: SupportedModelName): boolean {
    const permission = {
      invite: Permission.VISITORS_INVITE_BLOCKLIST_APPROVAL_REVIEW,
      entry: Permission.VISITORS_ENTRY_BLOCKLIST_APPROVAL_REVIEW,
    }[modelName];

    const { context } = this.model ?? {};

    const hasPermission =
      context === 'property'
        ? isPresent(_intersection(ALLOWED_ROLES, this.currentAdmin.roleNames))
        : this.authz.hasPermissionAtCurrentLocation(permission);

    return (hasPermission || this.#isContact()) && this.#hasReportsWithMatchingContext;
  }

  get canManageProperty(): boolean {
    const { roleNames } = this.currentAdmin;
    return isPresent(_intersection(CAN_MANAGE_PROPERTY, roleNames));
  }

  get #hasReportsWithMatchingContext(): boolean {
    if (!this.model) return true;

    const { context, report, visitApproval } = this.model || {};

    const blacklistFilterSource =
      context === 'property' ? BlocklistFilterSource.PROPERTY : BlocklistFilterSource.LOCATION;

    if (visitApproval) {
      const blocklistResult = visitApproval.blocklistResult;
      // V1 legacy emits a phantom matchedFilters entry with all-null fields on a clean pass;
      // V2 emits []. Short-circuit on decision === 'pass' so both pipelines agree there is
      // nothing to review when the blocklist did not match.
      if (!blocklistResult || blocklistResult.decision === 'pass') return false;

      const matchingReports =
        blocklistResult.report.matchedFilters?.filter((filter) => {
          const reportFilterSource = filter.filterSource ?? BlocklistFilterSource.LOCATION;
          return reportFilterSource === blacklistFilterSource;
        }) ?? [];
      return matchingReports.length > 0;
    } else {
      const matchingReports =
        report?.filter((report) => {
          // only look at blocklist reports
          if (report.source !== 'blacklist') return false;
          const reportFilterSource = report.blacklistFilterSource ?? BlocklistFilterSource.LOCATION;
          return reportFilterSource === blacklistFilterSource;
        }) ?? [];
      return matchingReports.length > 0;
    }
  }

  #isContact(): boolean {
    const blocklistContacts = this.state.currentLocation?.blocklistContacts.content || [];
    return !!blocklistContacts.find((contact) => contact.id === this.currentAdmin.id);
  }
}
